import {
  App,
  Form,
  type TableColumnsType,
  type TablePaginationConfig
} from "antd"
import type { FilterValue, SorterResult } from "antd/es/table/interface"
import React, { useCallback, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { CardComponent } from "../../../../../components/Card/CardComponent"
import { EntityTable } from "../../../../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../../../components/EntityTable/surfaces.ts"
import { MODAL_CONFIRM_CONFIG } from "../../../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import { TableAddAction } from "../../../../../components/Table/TableAddAction.tsx"
import { TableResultCount } from "../../../../../components/Table/TableResultCount.tsx"
import { excludeDeletedAccounts } from "../../../../../constants/deleted-account-ui.ts"
import { useScrolledTable } from "../../../../../hooks/use-scrolled-table.ts"
import { cn } from "../../../../../utils/cn.ts"
import { isFormChanged } from "../../../../../utils/object/is-form-changed.ts"
import { getTableOrderingFromSorter, withSortOrder } from "../../../../../utils/table"
import { LEAD_STATUSES } from "../../../../Lead/constants/lead-statuses.ts"
import type { UserInfo } from "../../../../LoginPage/types/auth.types.ts"
import type {
  BulkLeadActionParams,
  BulkLeadResult
} from "../../../api/services.types.ts"
import { LeadsEmpty } from "../../../components/LeadsEmpty.tsx"
import {
  LEADS_FILTER_KEYS,
  clearedActiveFilters,
  countActiveFilters
} from "../../../redux/leads.constants.ts"
import type {
  LeadsFilterOptions,
  LeadsFilters
} from "../../../redux/leads.types.ts"
import { type LeadListItem } from "../../../types/types.ts"
import { LeadListTableFilters } from "./LeadListTableFilters.tsx"
import { LeadSmartFilters } from "./LeadSmartFilters.tsx"
import {
  type BulkLeadFacts,
  LeadsBulkActionBar
} from "./LeadsBulkActionBar.tsx"
import {
  type LeadEditableData,
  LeadsListEditableRow
} from "./LeadsListEditableRow.tsx"
import { getColumns } from "./columns.tsx"
import {
  LEAD_SMART_FILTERS,
  isSmartFilterActive
} from "./lead-smart-filters.ts"
import { LEADS_TABLE_MIN_WIDTH } from "./table-width.ts"

interface LeadListTableProps {
  editableLead: null | LeadEditableData
  loading: boolean
  filterOptions: LeadsFilterOptions
  filters: LeadsFilters
  leads: LeadListItem[]
  count: number
  selectedIds: string[]
  onFiltersChange: (filters: Partial<LeadsFilters>) => void
  next?: string | null
  retryNext?: string | null
  onLoadMore: () => void
  onOrderingChange: (ordering: string | null) => void
  onSelectRow: (record: LeadListItem, action: string, payload?: unknown) => void
  onEditRow: (record: LeadEditableData) => void
  onSetEditableLead: (editableLead: null | LeadEditableData) => void
  onSelectedIdsChange: (ids: string[]) => void
  onBulkAction: (params: BulkLeadActionParams) => Promise<BulkLeadResult>
  onExportSelected: (ids: string[]) => Promise<void>
  onAddLead: () => void
  //
  smartFilterCounts?: Record<string, number> | null
  createAllowed?: boolean
  editAllowed?: boolean
  deleteAllowed?: boolean
  exporting?: boolean
  assignedSalesAllowed?: boolean
  hideSalesFilter?: boolean
  showConvertedAllowed?: boolean
  //
  ordering: string | null
  user?: UserInfo | null
}

export default function LeadsListTable({
  editableLead,
  loading,
  filterOptions,
  filters,
  leads,
  count,
  selectedIds,
  user,
  smartFilterCounts,
  createAllowed,
  editAllowed,
  deleteAllowed,
  exporting,
  assignedSalesAllowed,
  hideSalesFilter,
  showConvertedAllowed,
  onFiltersChange,
  next,
  retryNext,
  onLoadMore,
  ordering,
  onOrderingChange,
  onSetEditableLead,
  onSelectRow,
  onEditRow,
  onSelectedIdsChange,
  onBulkAction,
  onExportSelected,
  onAddLead
}: LeadListTableProps) {
  const { t } = useTranslation()
  const { modal } = App.useApp()

  // Naming the chip in force turns "no leads" from a dead end into something
  // the reader can act on.
  const activeSmartFilterKey = useMemo(
    () =>
      LEAD_SMART_FILTERS.find((preset) =>
        isSmartFilterActive(preset, filters, { userId: user?.id })
      )?.labelKey ?? null,
    [filters, user?.id]
  )
  const [form] = Form.useForm<LeadEditableData>()
  const statuses = filterOptions?.status || []
  const sources = filterOptions?.source || []

  const onChangeTable = useCallback(
    (
      _pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<LeadListItem> | SorterResult<LeadListItem>[]
    ) => {
      onOrderingChange(getTableOrderingFromSorter(sorter))
    },
    [onOrderingChange]
  )

  const onCancel = useCallback(
    (callback?: () => void) => {
      if (!editableLead) return onSetEditableLead(null)
      const values = form.getFieldsValue()

      const isChanged = isFormChanged<LeadEditableData>(values, editableLead)

      if (isChanged) {
        modal.confirm({
          ...MODAL_CONFIRM_CONFIG.cancelChanges,
          onOk() {
            onSetEditableLead(null)
            callback?.()
          }
        })
      } else {
        onSetEditableLead(null)
        callback?.()
      }
    },
    [editableLead, form, modal, t]
  )

  const onUpdate = useCallback(async () => {
    try {
      if (!editableLead) return

      const row = await form.validateFields()
      const isChanged = isFormChanged<LeadEditableData>(row, editableLead)

      if (isChanged) {
        onEditRow({ ...row, id: editableLead?.id })
      } else {
        onSetEditableLead(null)
      }
    } catch (e) {
      console.log(e)
    }
  }, [editableLead, form, onEditRow])

  const onSelectRowHandler = useCallback(
    (record: LeadListItem, action: string, payload?: unknown) => {
      const initEdit = () => {
        const initialValues: LeadEditableData = {
          ...record,
          sales_manager: excludeDeletedAccounts(record?.sales_manager),
          status: filterOptions?.["status"]?.find(
            (s) => s?.label === record?.status
          )?.value,
          source: filterOptions?.["source"]?.find(
            (s) => s?.label === record?.source
          )?.value
        }

        form.setFieldsValue(
          initialValues as unknown as Parameters<typeof form.setFieldsValue>[0]
        )
        return onSetEditableLead(initialValues)
      }

      switch (action) {
        case "edit_row": {
          const activeStatus = statuses?.find(
            (s) => s?.label === record?.status
          )

          const isEditable = activeStatus?.value !== LEAD_STATUSES.WON
          if (!isEditable) return

          if (editableLead) return onCancel(initEdit)
          else return initEdit()
        }
        case "save_row":
          return onUpdate()
        case "cancel":
          return onCancel()
        default:
          onSelectRow(record, action, payload)
      }
    },
    [
      editableLead,
      form,
      statuses,
      onCancel,
      onSetEditableLead,
      onSelectRow,
      filterOptions,
      onUpdate
    ]
  )

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: selectedIds,
      preserveSelectedRowKeys: true,
      onChange: (keys: React.Key[]) => onSelectedIdsChange(keys.map(String))
    }),
    [selectedIds, onSelectedIdsChange]
  )

  const [bulkBarVisible, setBulkBarVisible] = useState(false)

  // The paginator and the selection toolbar sit inside the card, below the
  // table body. The body has to give up their height or the card outgrows the
  // viewport and pushes them past the fold — where the page, having no
  // scrollport of its own, leaves no way to reach them (EAD-418).
  // Undefined rather than zero when unreadable: zero would still take the
  // viewport branch, but with no allowance for the header.
  const stickyChrome = useMemo(() => {
    const declared = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue(
        "--header-height"
      )
    )
    return Number.isFinite(declared) ? declared : undefined
  }, [])

  const { tableRef, tableHeight, onTableScroll } = useScrolledTable({
    loading,
    next,
    loadMore: onLoadMore,
    fullPage: true,
    fillsViewportBelow: stickyChrome
  })

  const tableSettings = useMemo(
    () => ({
      scroll: {
        x: LEADS_TABLE_MIN_WIDTH,
        y: tableHeight,
        scrollToFirstRowOnChange: true
      },
      onRow: (record: LeadListItem) => {
        return {
          record,
          onSelectRow: onSelectRowHandler,
          onDoubleClick: editAllowed
            ? (event: React.MouseEvent<HTMLElement>) => {
                // Toggling the checkbox quickly reads as a double-click on the
                // row, which would otherwise drop it into inline edit.
                const target = event.target as HTMLElement
                if (target?.closest?.(".ant-table-selection-column")) return
                onSelectRowHandler(record, "edit_row")
              }
            : undefined
        }
      }
    }),
    [onSelectRowHandler, editAllowed, tableHeight]
  )

  const tableColumns: TableColumnsType<LeadListItem> = useMemo(() => {
    return withSortOrder(
      getColumns({
        t,
        onSelectRow: onSelectRowHandler,
        editAllowed,
        archiveLabel: t("leadArchive.action"),
        statuses,
        sources
      }),
      ordering
    )
  }, [onSelectRowHandler, editAllowed, statuses, sources, t, ordering])

  // A selection survives paging, so a failure can name a lead the current page
  // no longer holds. Names seen on earlier pages are kept rather than falling
  // back to a raw id.
  const seenLeadNames = useRef<Record<string, string>>({})
  const leadNames = useMemo(() => {
    const next = { ...seenLeadNames.current }
    leads.forEach((lead) => {
      if (lead.lead_name) next[lead.id] = lead.lead_name
    })
    seenLeadNames.current = next
    return next
  }, [leads])

  const seenLeadFacts = useRef<Record<string, BulkLeadFacts>>({})
  const leadFacts = useMemo(() => {
    const statusValues = new Map(
      statuses.map(({ label, value }) => [label, value])
    )
    const next = { ...seenLeadFacts.current }
    leads.forEach((lead) => {
      const status = statusValues.get(lead.status)
      if (status == null) return
      next[lead.id] = { status, converted: !!lead.is_project }
    })
    seenLeadFacts.current = next
    return next
  }, [leads, statuses])

  return (
    <CardComponent classNames={{ body: "p-0" }}>
      <LeadSmartFilters
        filters={filters}
        counts={smartFilterCounts}
        userId={user?.id}
        filterOptions={filterOptions}
        onFiltersChange={onFiltersChange}
      />
      <LeadListTableFilters
        filters={filters}
        filterOptions={filterOptions}
        onFilersChange={onFiltersChange}
        hideSalesFilter={hideSalesFilter}
        showConvertedAllowed={showConvertedAllowed}
      />
      {/* Outside the measured block on purpose: the action's own strip is not
          footer the body has to give its height up for. */}
      <TableAddAction
        label={t("leadsTable.actions.addLead")}
        // The bulk bar takes the corner the "+" is anchored to, for as long as
        // it is up — which outlasts the selection, since it goes on reporting
        // the outcome after one. Left undecided otherwise, so an unresolved
        // create right keeps the control's own default rather than hiding it.
        allowed={bulkBarVisible ? false : createAllowed}
        disabled={!!editableLead}
        onAdd={onAddLead}
      >
        <div ref={tableRef}>
          <Form
            form={form}
            component={false}
            className={"with-default-error"}
            initialValues={editableLead ? editableLead : {}}
          >
            <EntityTable<LeadListItem>
              surface={TABLE_SURFACES.leadsActive}
              tab={DEFAULT_TAB}
              components={{
                body:
                  leads?.length && editAllowed
                    ? {
                        row: (props) => (
                          <LeadsListEditableRow
                            filterOptions={filterOptions}
                            editingID={editableLead?.id}
                            assignedSalesAllowed={assignedSalesAllowed}
                            {...props}
                          />
                        )
                      }
                    : {}
              }}
              columns={tableColumns}
              loading={loading && !leads?.length}
              dataSource={leads}
              rowSelection={rowSelection}
              selectionMode="on-demand"
              scroll={tableSettings.scroll}
              onChange={onChangeTable}
              onScroll={onTableScroll}
              withOutPagination
              pagination={false}
              onRow={tableSettings.onRow}
              rowHoverable={true}
              className={cn({
                "[&_.ant-table-cell-scrollbar]:w-0 [&_.ant-table-cell-scrollbar]:p-1":
                  !leads?.length
              })}
              locale={{
                emptyText: (
                  <LeadsEmpty
                    hasSearch={!!filters?.q}
                    activeFilterLabel={
                      activeSmartFilterKey ? t(activeSmartFilterKey) : null
                    }
                    activeFilterCount={countActiveFilters(
                      filters,
                      LEADS_FILTER_KEYS
                    )}
                    onClearFilters={() =>
                      onFiltersChange(clearedActiveFilters())
                    }
                  />
                )
              }}
            />
          </Form>
          <TableResultCount
            underAddAction
            loaded={leads?.length ?? 0}
            total={count}
            loading={loading}
            next={next}
            retryNext={retryNext}
            onRetry={onLoadMore}
          />
          <LeadsBulkActionBar
            onVisibleChange={setBulkBarVisible}
            selectedIds={selectedIds}
            leadNames={leadNames}
            leadFacts={leadFacts}
            filterOptions={filterOptions}
            editAllowed={editAllowed}
            deleteAllowed={deleteAllowed}
            exporting={exporting}
            onClear={() => onSelectedIdsChange([])}
            onAction={onBulkAction}
            onExport={onExportSelected}
          />
        </div>
      </TableAddAction>
    </CardComponent>
  )
}
